import React, { createContext, ReactNode, useContext, useEffect } from "react";
import { useParams } from "react-router-dom";
import { setLastBoardId } from "@utils/boardPreferences";

interface BoardContextValue {
  boardId: string | undefined;
}

const BoardContext = createContext<BoardContextValue | null>(null);

interface BoardProviderProps {
  children: ReactNode;
  /** Provide boardId directly (e.g. in Storybook or tests). When omitted, boardId is read from the route param. */
  boardId?: string;
}

/**
 * Provides the current board ID to the subtree.
 *
 * In production, mount this inside a `/:boardId` route and omit the prop —
 * the boardId is extracted from the URL once here and shared via context.
 *
 * In Storybook / tests, pass `boardId` directly to skip the routing dependency.
 *
 * Every board route mounts this provider, so it is also where the board is
 * recorded as the one to reopen on the next load.
 */
export const BoardProvider: React.FC<BoardProviderProps> = ({
  children,
  boardId: boardIdProp,
}) => {
  const params = useParams<{ boardId?: string }>();
  const boardId = boardIdProp ?? params.boardId;
  const routeBoardId = params.boardId;

  // Only a board reached through the router is remembered: a boardId passed as
  // a prop is a Storybook or test fixture, not somewhere the user navigated.
  useEffect(() => {
    if (routeBoardId) {
      setLastBoardId(routeBoardId);
    }
  }, [routeBoardId]);

  return (
    <BoardContext.Provider value={{ boardId }}>
      {children}
    </BoardContext.Provider>
  );
};

/**
 * Returns the current board ID from the nearest BoardProvider.
 * Throws if called outside of a BoardProvider.
 */
export const useBoardId = (): string | undefined => {
  const ctx = useContext(BoardContext);
  if (ctx === null) {
    throw new Error("useBoardId must be used within a BoardProvider");
  }
  return ctx.boardId;
};
